import makeConcurrent from 'make-concurrent';
import proxyFreeze from '@exodus/proxy-freeze';
import { isSetter } from './utils/guards.js';
const isReadonlySetOrMap = (value) => ['[object ReadonlySet]', '[object ReadonlyMap]'].includes(Object.prototype.toString.call(value));
const freeze = (value) => (isReadonlySetOrMap(value) ? value : proxyFreeze(value));
const withChangeDetection = (listener) => {
    let currentValue;
    let called = false;
    return async (value) => {
        if (called && value === currentValue)
            return;
        called = true;
        currentValue = value;
        await listener(currentValue);
    };
};
const enforceObservableRules = ({ defaultValue, makeGetNonConcurrent = false, getInitialized = () => true, ...atom }) => {
    const enqueue = makeConcurrent((fn) => fn(), { concurrency: 1 });
    const postProcessValue = (value = defaultValue) => value && typeof value === 'object' ? freeze(value) : value;
    const nonConcurrentGet = makeGetNonConcurrent
        ? makeConcurrent(atom.get)
        : atom.get;
    const get = () => nonConcurrentGet().then(postProcessValue);
    const observe = (listener) => {
        let called = false;
        let valueEmittedFromGet;
        let subscribed = true;
        listener = withChangeDetection(listener);
        const publishSerially = (value) => {
            called = true;
            return enqueue(() => listener(value));
        };
        void nonConcurrentGet().then((value) => {
            if (!subscribed)
                return;
            if (!called) {
                valueEmittedFromGet = value;
                publishSerially(postProcessValue(value));
            }
        });
        const subscriber = (value) => {
            if (valueEmittedFromGet !== undefined) {
                const isAlreadyEmitted = value === valueEmittedFromGet;
                valueEmittedFromGet = undefined;
                if (isAlreadyEmitted) {
                    return;
                }
            }
            return publishSerially(postProcessValue(value));
        };
        Object.defineProperty(subscriber, 'resetCallState', {
            value: () => (called = false),
        });
        const unsubscribe = atom.observe(subscriber);
        return () => {
            unsubscribe();
            subscribed = false;
        };
    };
    const set = makeConcurrent(async (value) => {
        if (isSetter(value)) {
            const current = getInitialized() ? await get() : defaultValue;
            value = await value(current);
            if (current === value)
                return;
        }
        await atom.set(value);
    });
    return {
        get,
        set,
        reset: () => set(undefined),
        observe,
    };
};
export default enforceObservableRules;
